import { Component, ChangeDetectionStrategy, computed, effect, inject, signal } from '@angular/core';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DatePipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { WidgetsService } from '@components/widgets/widgets.service';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import { VehicleStore } from '@modules/core/store/vehicle.store';
import { TranslateService } from '@modules/core/services/translate.service';
import { TranslateModule } from '@ngx-translate/core';
import { Vehicle } from '@modules/shared/models/vehicle';
import { VehicleWarning, WarningMessageType } from '@modules/shared/models/vehicle/vehicle-warning';
import { WarningMessageHelper } from '@modules/shared/helpers/warning-message-helper';
import { SpinnerComponent } from '@modules/shared/components/spinner/spinner.component';
import { BaseWidgetDirective } from '@components/widgets/widgetsbase.directive';
import { WidgetDropdownDirective } from '@components/widgets/widget-dropdown.directive';
import { WarningIconComponent } from '@modules/shared/components/warning-icon/warning-icon.component';
import { MatTooltipModule } from '@angular/material/tooltip';

const SORT_STATE_KEY = 'oms.widget.warnings.sort';

export interface WarningItem {
    vehicle: Vehicle;
    warning: VehicleWarning;
}

// Priority order for displayed warnings â€” highest priority first.
// Edit this list to change which types appear and in what order.
const WARNING_PRIORITY: WarningMessageType[] = [
    WarningMessageType.ImmobilizerBypass,
    WarningMessageType.TripNotStartedWarning,
    WarningMessageType.SignOnMissingWarning,
    WarningMessageType.OffRouteWarning,
    WarningMessageType.NoParkingSpotFound,
    WarningMessageType.UnknownVehicleCon,
    WarningMessageType.VehicleDelay,
    WarningMessageType.SocWarning,
    WarningMessageType.Other,
];

@Component({
    selector: 'app-warnings-widget',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SpinnerComponent, TranslateModule, DatePipe, WarningIconComponent, BaseWidgetDirective,
        DragDropModule, MatTooltipModule, WidgetDropdownDirective
    ],
    templateUrl: './warnings-widget.component.html',
    styleUrls: ['./warnings-widget.component.scss'],
})
export class WarningsWidgetComponent {
    public readonly warningHelper = inject(WarningMessageHelper);
    public readonly widgetState = inject(WidgetsService);
    private readonly _ts = inject(TranslateService);
    private readonly _vehicleStore = inject(VehicleStore);

    public readonly widgetTitle = signal<string>('');
    public readonly widgetId = signal<string>('widget-warnings');
    public readonly fillRemaining = signal<boolean>(false);
    public readonly fixedSize = signal<boolean>(false);
    public readonly defaultFraction = signal<number>(0.2);
    public readonly sortCol = signal<'inv' | 'route' | 'type' | 'time'>('time');
    public readonly sortDir = signal<'asc' | 'desc'>('desc');
    public readonly badgePulse = signal(false);
    public readonly manualSort = signal<boolean>(false);
    public readonly customPriority = signal<WarningMessageType[]>([...WARNING_PRIORITY]);

    public readonly sortActive = computed(() =>
        this.manualSort() || this.sortCol() !== 'time' || this.sortDir() !== 'desc'
    );

    public readonly warnings = computed<WarningItem[]>(() => {
        const vehicles = this._vehicles();
        if (vehicles == undefined) return undefined;
        const isManual = this.manualSort();
        const priority = isManual ? this.customPriority() : WARNING_PRIORITY;

        const items: WarningItem[] = [];
        for (const vehicle of vehicles) {
            for (const warning of vehicle.displayableWarnings) {
                if (!priority.includes(warning.messageType)) continue;
                items.push({ vehicle, warning });
            }
        }

        if (isManual) {
            items.sort((a, b) => {
                const pa = priority.indexOf(a.warning.messageType);
                const pb = priority.indexOf(b.warning.messageType);
                if (pa !== pb) return pa - pb;
                // secondary: newest first within the same type
                return new Date(b.warning.measuredAt).getTime() - new Date(a.warning.measuredAt).getTime();
            });
        } else {
            const col = this.sortCol();
            const dir = this.sortDir();
            items.sort((a, b) => {
                let cmp = 0;
                switch (col) {
                    case 'inv':   cmp = a.vehicle.inventoryNumberWithoutPrefix.localeCompare(b.vehicle.inventoryNumberWithoutPrefix); break;
                    case 'route': cmp = (a.vehicle.routeNumber ?? '').localeCompare(b.vehicle.routeNumber ?? ''); break;
                    case 'type': {
                        const pa = WARNING_PRIORITY.indexOf(a.warning.messageType);
                        const pb = WARNING_PRIORITY.indexOf(b.warning.messageType);
                        cmp = pa - pb;
                        break;
                    }
                    case 'time':  cmp = new Date(a.warning.measuredAt).getTime() - new Date(b.warning.measuredAt).getTime(); break;
                }
                return dir === 'asc' ? cmp : -cmp;
            });
        }

        return items;
    });

    private _prevWarningCount = 0;
    private _pulseTimer: ReturnType<typeof setTimeout> = undefined;
    private readonly _vehicles = toSignal(
        this._vehicleStore.getVehiclesState(),
        { initialValue: undefined }
    );

    constructor() {
        this.widgetTitle.set(this._ts.instant(_translate('Warnings')));
        this._loadSortState();
        effect(() => {
            const w = this.warnings();
            if (w == undefined) return;
            if (w.length > this._prevWarningCount) {
                if (this._pulseTimer != undefined) clearTimeout(this._pulseTimer);
                this.badgePulse.set(true);
                this._pulseTimer = setTimeout(() => this.badgePulse.set(false), 700);
            }
            this._prevWarningCount = w.length;
        });
    }

    public setSort(col: 'inv' | 'route' | 'type' | 'time'): void {
        if (this.sortCol() === col) {
            this.sortDir.update(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            this.sortCol.set(col);
            this.sortDir.set('asc');
        }
    }

    public toggleManualSort(): void {
        this.manualSort.update(v => !v);
        this._saveSortState();
    }

    public resetSort(): void {
        this.manualSort.set(false);
        this.customPriority.set([...WARNING_PRIORITY]);
        this.sortCol.set('time');
        this.sortDir.set('desc');
        this._saveSortState();
    }

    public onTypeDrop(event: CdkDragDrop<WarningMessageType[]>): void {
        const arr = [...this.customPriority()];
        moveItemInArray(arr, event.previousIndex, event.currentIndex);
        this.customPriority.set(arr);
        this._saveSortState();
    }

    private _loadSortState(): void {
        try {
            const raw = localStorage.getItem(SORT_STATE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (parsed.manualSort !== undefined) {
                this.manualSort.set(!!parsed.manualSort);
            }
            if (Array.isArray(parsed.customPriority)) {
                const saved = parsed.customPriority as WarningMessageType[];
                const isValidPermutation =
                    WARNING_PRIORITY.every(t => saved.includes(t)) &&
                    saved.every(t => WARNING_PRIORITY.includes(t)) &&
                    saved.length === WARNING_PRIORITY.length;
                if (isValidPermutation) {
                    this.customPriority.set(saved);
                }
            }
        } catch { /* ignore corrupt storage */ }
    }

    private _saveSortState(): void {
        try {
            localStorage.setItem(SORT_STATE_KEY, JSON.stringify({
                manualSort: this.manualSort(),
                customPriority: this.customPriority()
            }));
        } catch { /* ignore storage errors */ }
    }

}
